import axios from "axios";
import { apiUrl } from "./config";

const API_BASE = apiUrl;
const CUSTOMER_AUTH_COOKIE_KEY = "gamapa_customer_user";
const ADMIN_AUTH_COOKIE_KEY = "gamapa_admin_user";

/* -------------------------------------------------------------------------- */
/*                            COOKIE HELPERS                                  */
/* -------------------------------------------------------------------------- */

const getCookieValue = (key) => {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${key}=`));
  return cookie ? decodeURIComponent(cookie.split("=")[1]) : null;
};

const setCookieValue = (key, value, days = 7) => {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const removeCookieValue = (key) => {
  if (typeof document === "undefined") return;
  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const parseCookieUser = (cookieKey) => {
  try {
    const rawUser = getCookieValue(cookieKey);
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    console.error(`Unable to parse auth cookie for ${cookieKey}:`, error);
    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                            USER MAPPING                                    */
/* -------------------------------------------------------------------------- */

const mapBackendUser = (user) => {
  const nameParts = (user?.name || "").trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ");

  return {
    id: user.id,
    firstName,
    lastName,
    email: user.email || "",
    phone: user.phone || "",
    address: user.address || "Add your delivery address",
    addresses: user.addresses || [],
    joined: user.joined || "New member",
    // role is the full role object { id, name, permissions } or null
    role: user.role || null,
    role_id: user.role_id || null,
    name: user.name || `${firstName} ${lastName}`.trim(),
  };
};

/* -------------------------------------------------------------------------- */
/*                            SESSION ACCESSORS                               */
/* -------------------------------------------------------------------------- */

export const getCustomerSessionUser = () => parseCookieUser(CUSTOMER_AUTH_COOKIE_KEY);
export const getAdminSessionUser    = () => parseCookieUser(ADMIN_AUTH_COOKIE_KEY);

export const setCustomerSessionUser = (user) =>
  setCookieValue(CUSTOMER_AUTH_COOKIE_KEY, JSON.stringify(user));

export const setAdminSessionUser = (user) =>
  setCookieValue(ADMIN_AUTH_COOKIE_KEY, JSON.stringify(user));

export const clearCustomerSessionUser = () => removeCookieValue(CUSTOMER_AUTH_COOKIE_KEY);
export const clearAdminSessionUser    = () => removeCookieValue(ADMIN_AUTH_COOKIE_KEY);

/* -------------------------------------------------------------------------- */
/*                            AUTH FUNCTIONS                                  */
/* -------------------------------------------------------------------------- */

export const registerStoredUser = async ({ firstName, lastName, email, password, phone }) => {
  try {
    const response = await axios.post(`${API_BASE}/user`, {
      name: `${firstName.trim()} ${lastName.trim()}`.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim(),
      password,
      license_id: 3,
    });

    if (!response.data?.status) {
      return { success: false, message: response.data?.error || "User registration failed." };
    }

    return { success: true, user: mapBackendUser(response.data.user) };
  } catch (error) {
    return { success: false, message: error.response?.data?.error || "Unable to register user right now." };
  }
};

export const loginCustomerUser = async ({ email, password }) => {
  try {
    const response = await axios.post(`${API_BASE}/user/login`, {
      email: email.trim().toLowerCase(),
      password,
    });

    if (!response.data?.status) {
      return { success: false, message: response.data?.error || "Invalid email or password." };
    }

    const user = mapBackendUser(response.data.user);

    // Block users who have an admin role from using the customer login
    if (user.role) {
      return { success: false, message: "This account has admin access. Please use the admin login page." };
    }

    return { success: true, user };
  } catch (error) {
    return { success: false, message: error.response?.data?.error || "Unable to login right now." };
  }
};

export const loginAdminUser = async ({ email, password }) => {
  try {
    const response = await axios.post(`${API_BASE}/user/login`, {
      email: email.trim().toLowerCase(),
      password,
    });

    if (!response.data?.status) {
      return { success: false, message: response.data?.error || "Invalid email or password." };
    }

    const user = mapBackendUser(response.data.user);

    // Only allow users who have a role assigned
    if (!user.role) {
      return { success: false, message: "You don't have admin access. Contact your administrator." };
    }

    return { success: true, user };
  } catch (error) {
    return { success: false, message: error.response?.data?.error || "Unable to login right now." };
  }
};

// A user is an "admin" (can access admin panel) if they have any role assigned
export const isAdminUser = (user) => Boolean(user?.role);

export const updateStoredUser = async (updatedUser) => {
  const fullName = `${updatedUser.firstName || ""} ${updatedUser.lastName || ""}`.trim();

  try {
    const payload = {
      id: updatedUser.id,
      name: fullName,
      email: updatedUser.email,
      phone: updatedUser.phone,
    };

    if (Array.isArray(updatedUser.addresses)) {
      payload.addresses = updatedUser.addresses;
    }

    const response = await axios.put(`${API_BASE}/user`, payload);

    if (!response.data?.status) return updatedUser;

    return {
      ...updatedUser,
      ...mapBackendUser(response.data.user),
      address: updatedUser.address,
      addresses: updatedUser.addresses || [],
    };
  } catch (error) {
    return updatedUser;
  }
};
